import { For, If, location, onMount, reactive } from 'cdui-js';
import { replaceTemplate } from '@china-difi/lib/template';
import i18n from '../../../i18n';
import {
  cancelStockTransferInApply,
  fetchStockTransferInApplyDetail,
  fetchStockTransferOutBrokers,
} from '../../../api/stockTransferIn';
import { isInAppWebView } from '../../../analytics/appInfo';
import { callPhone, toDialNumber } from '../utils/common';
import { formatServerDateTime } from '../../../lib/date';
import { routeReplace } from '../../../lib/browser';
import { DiFiLoading } from '../../../components/DiFi/DiFiLoading/index.tsx';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { showModal } from '../components/Modal';
import { showToast, showToastError } from '../components/Toast';
import {
  ResultCancelledIcon,
  ResultFailIcon,
  ResultPendingIcon,
  ResultSuccessIcon,
} from '../account/result/icons';
import { StiInfoRow, StiInfoSectionHead, StiStockInfoCards } from './InfoBlocks';
import { StockTransferNav } from './Nav';
import {
  canCancelStockTransferInApply,
  clearStockTransferSubmittedSnapshot,
  displayInAccountTypeForDraft,
  displayTransferField,
  getStockTransferSubmittedSnapshot,
  statementProofFileName,
  normalizeStockTransferApplyId,
  readStockTransferQueryParam,
  displayInboundCcassIdForTransfer,
  resolveInAccountNum,
  resolveOutBrokerDialPhone,
  snapshotFromApplyDetail,
  stockTransferStatusVisualKind,
  syncStockTransferAppShell,
  wfStatusDetailTitle,
  withStockTransferQuery,
  type StockTransferSubmittedSnapshot,
  type StockTransferStatusVisualKind,
} from './shared';
import './page.css';

const renderStatusIcon = (kind: StockTransferStatusVisualKind) => {
  if (kind === 'success') return <ResultSuccessIcon />;
  if (kind === 'pending') return <ResultPendingIcon />;
  if (kind === 'cancelled') return <ResultCancelledIcon />;
  return <ResultFailIcon />;
};

export const StockTransferInSubmitted = () => {
  const state = reactive({
    snapshot: null as StockTransferSubmittedSnapshot | null,
    fromRecords: false,
    loading: false,
    outBrokerNotified: false,
    /** 记录详情进页时从券商清单补全的 contactPhone */
    outBrokerCatalogPhoneOverride: '',
    cancelling: false,
  });

  const draft = () => state.snapshot?.draft;
  const market = () => state.snapshot?.market ?? 'HK';

  const onClose = () => {
    if (state.fromRecords) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/records'), [0, 0]);
      return;
    }
    clearStockTransferSubmittedSnapshot();
    routeReplace(withStockTransferQuery('/h5/stock-transfer-in'));
  };

  const outBrokerDialPhone = () => {
    const d = draft();
    if (!d) return '';
    const catalog =
      state.outBrokerCatalogPhoneOverride.trim() || String(d.outBrokerCatalogContactPhone ?? '').trim();
    return resolveOutBrokerDialPhone({
      outBrokerCatalogContactPhone: catalog,
      outBrokerContactPhone: d.outBrokerContactPhone,
    });
  };

  const contactOutBrokerTemplateVars = () => ({
    broker: displayTransferField(draft()?.outBrokerName),
  });

  const dialOutBrokerPhone = () => {
    const dial = toDialNumber(outBrokerDialPhone());
    if (!dial) {
      showToast(i18n.StockTransferIn.OutBrokerPhoneEmpty);
      return;
    }
    if (isInAppWebView()) {
      callPhone(dial);
      return;
    }
    window.location.href = `tel:${dial}`;
  };

  const onNotifiedOutBroker = () => {
    if (state.outBrokerNotified) return;
    state.outBrokerNotified = true;
    showToast(i18n.StockTransferIn.NotifiedOutBrokerToast);
  };

  const canWithdrawApply = () => {
    const snap = state.snapshot;
    if (!snap?.applyId?.trim()) return false;
    return canCancelStockTransferInApply(snap.status);
  };

  const statusTitle = () => wfStatusDetailTitle(state.snapshot?.status);

  const statusVisualKind = () => stockTransferStatusVisualKind(state.snapshot?.status);

  const showSubmittedStatusDesc = () => {
    const kind = statusVisualKind();
    return kind === 'success' || kind === 'pending';
  };

  const backAfterWithdraw = () => {
    clearStockTransferSubmittedSnapshot();
    location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/records'), [0, 0]);
  };

  const onWithdrawApply = () => {
    const applyId = state.snapshot?.applyId?.trim();
    if (!applyId || state.cancelling || !canWithdrawApply()) return;
    showModal({
      title: i18n.StockTransferIn.WithdrawApplyTitle,
      content: () => i18n.StockTransferIn.WithdrawApplyConfirm,
      cancelText: i18n.FundH5.No,
      confirmText: i18n.FundH5.Yes,
      onConfirm: () => {
        if (state.cancelling) return;
        state.cancelling = true;
        void cancelStockTransferInApply(applyId)
          .then(() => {
            showToast(i18n.StockTransferIn.WithdrawApplySuccess);
            backAfterWithdraw();
          })
          .catch((e) => {
            showToastError(e);
            state.cancelling = false;
          });
      },
    });
  };

  const hydrateOutBrokerCatalogPhone = async (outBrokerId: number) => {
    if (!outBrokerId) return;
    try {
      const list = await fetchStockTransferOutBrokers();
      const hit = list.find((row) => Number(row.id) === outBrokerId);
      const phone = String(hit?.contactPhone ?? '').trim();
      if (phone) state.outBrokerCatalogPhoneOverride = phone;
    } catch {
      // 清单失败仍可用用户填写电话
    }
  };

  onMount(async () => {
    syncStockTransferAppShell();
    const from = readStockTransferQueryParam('from');
    const applyId = normalizeStockTransferApplyId(readStockTransferQueryParam('applyId'));
    if (from === 'records' && applyId) {
      state.fromRecords = true;
      state.loading = true;
      try {
        await bootDepositApp();
        const detail = await fetchStockTransferInApplyDetail(applyId);
        state.snapshot = snapshotFromApplyDetail(detail);
        const brokerId = Number(state.snapshot?.draft.outBrokerId);
        if (brokerId > 0 && state.snapshot) {
          await hydrateOutBrokerCatalogPhone(brokerId);
        }
      } catch (e) {
        showToastError(e);
        location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/records'), [0, 0]);
      } finally {
        state.loading = false;
      }
      return;
    }

    const snap = getStockTransferSubmittedSnapshot();
    if (!snap) {
      location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/records'), [0, 0]);
      return;
    }
    state.snapshot = snap;
  });

  return (
    <div class="stock-transfer-in-h5">
      <StockTransferNav
        title={i18n.StockTransferIn.RecordDetailTitle}
        onBack={onClose}
        onClose={onClose}
        showRecords={!state.fromRecords}
      />
      <div class="sti-scroll sti-submitted-scroll">
        {state.loading ? (
          <div class="sti-records-loading">
            <DiFiLoading />
          </div>
        ) : null}
        <If when={!!state.snapshot && !state.loading}>
          <div class="sti-submitted-hero">
            <div class="sti-submitted-hero-icon">{renderStatusIcon(statusVisualKind())}</div>
            <div class="sti-submitted-hero-text">
              <h1 class="sti-submitted-hero-title">{statusTitle()}</h1>
              <If when={showSubmittedStatusDesc()}>
                <p class="sti-submitted-hero-desc">{i18n.StockTransferIn.SubmittedStatusDesc}</p>
              </If>
            </div>
            <p class="sti-submitted-submit-date color-D font-s-S">
              {i18n.StockTransferIn.LabelSubmitDate}: {formatServerDateTime(state.snapshot!.submittedAt)}
            </p>
          </div>

          <section class="sti-confirm-section sti-submitted-contact-section">
            <div class="sti-confirm-section-head">
              <div class="sti-submitted-contact-title">{i18n.StockTransferIn.ContactOutBrokerTitle}</div>
            </div>
            <div class="sti-submitted-contact">
              <ul class="sti-submitted-contact-list color-D font-s-S">
                <li>{replaceTemplate(i18n.StockTransferIn.ContactOutBrokerStep1, contactOutBrokerTemplateVars())}</li>
                <li>{replaceTemplate(i18n.StockTransferIn.ContactOutBrokerStep2, contactOutBrokerTemplateVars())}</li>
                <li>{replaceTemplate(i18n.StockTransferIn.ContactOutBrokerStep3, contactOutBrokerTemplateVars())}</li>
              </ul>
              <div
                class="sti-submitted-call-btn"
                role="button"
                tabindex={-1}
                onclick={dialOutBrokerPhone}
              >
                {replaceTemplate(i18n.StockTransferIn.CallInBroker, {
                  broker: displayTransferField(draft()?.outBrokerName),
                })}
              </div>
            </div>
          </section>

          <section class="sti-confirm-section">
            <StiInfoSectionHead title={i18n.StockTransferIn.SectionOutInfo} />
            <div class="sti-info-card">
              <StiInfoRow label={i18n.StockTransferIn.LabelBrokerName} value={displayTransferField(draft()?.outBrokerName)} />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelAccountNo}
                value={displayTransferField(draft()?.outBrokerAccountNo)}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelOutAccountName}
                value={displayTransferField(draft()?.outBrokerAccountName)}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelOutContactPerson}
                value={displayTransferField(draft()?.outBrokerContactPerson)}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelOutContactPhone}
                value={displayTransferField(draft()?.outBrokerContactPhone)}
              />
              <If when={(draft()?.statementProofs || []).length > 0}>
                <div class="sti-info-card-link-row">
                  <For each={draft()?.statementProofs || []}>
                    {(path, index) => (
                      <span class="sti-info-card-link">{statementProofFileName(path, index())}</span>
                    )}
                  </For>
                </div>
              </If>
            </div>
          </section>

          <section class="sti-confirm-section">
            <StiInfoSectionHead title={i18n.StockTransferIn.SectionInInfo} />
            <div class="sti-info-card">
              <StiInfoRow label={i18n.StockTransferIn.LabelBrokerName} value={displayTransferField(draft()?.inBrokerName)} />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelCcassId}
                value={displayTransferField(displayInboundCcassIdForTransfer(market(), draft()))}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelAccountType}
                value={displayTransferField(displayInAccountTypeForDraft(draft()))}
              />
              <StiInfoRow
                label={i18n.StockTransferIn.LabelAccountNo}
                value={displayTransferField(draft() ? resolveInAccountNum(draft()!) : '')}
              />
            </div>
          </section>

          <section class="sti-confirm-section">
            <StiInfoSectionHead title={i18n.StockTransferIn.SectionStockInfo} />
            <StiStockInfoCards stocks={state.snapshot!.selectedStocks} />
          </section>
        </If>
      </div>
      <If when={!!state.snapshot && !state.loading}>
        <div
          class={`sti-footer sti-submitted-footer${canWithdrawApply() ? ' sti-submitted-footer-dual' : ''}`}
        >
          <If when={canWithdrawApply()}>
            <button
              type="button"
              class="sti-submitted-withdraw-footer-btn"
              disabled={state.cancelling}
              onclick={onWithdrawApply}
            >
              {i18n.StockTransferIn.WithdrawApply}
            </button>
          </If>
          <button
            type="button"
            class={`primary-btn btn-rounded sti-submitted-notify-btn${state.outBrokerNotified ? ' sti-btn-disabled' : ''}`}
            disabled={state.outBrokerNotified}
            onclick={onNotifiedOutBroker}
          >
            {i18n.StockTransferIn.NotifiedOutBroker}
          </button>
        </div>
      </If>
    </div>
  );
};
